import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FileSearch, Sparkles, CheckCircle2, FileCheck } from 'lucide-react';
import './ProcessingAnimation.css';

/**
 * ProcessingAnimation Component
 * Shows animated steps during document processing:
 * 1. Upload Document
 * 2. Analyzing Document
 * 3. Extracting Information
 */
const ProcessingAnimation = () => {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Step 0: Upload (0-1s)
    const timer1 = setTimeout(() => setCurrentStep(1), 1000);
    // Step 1: Analyze (1-2s)
    const timer2 = setTimeout(() => setCurrentStep(2), 2000);
    // Step 2: Extract (2-3s) - Complete all steps
    const timer3 = setTimeout(() => setCurrentStep(3), 3000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, []);

  const steps = [
    {
      id: 'upload',
      label: 'Upload',
      icon: FileSearch,
      description: 'Uploading document...',
    },
    {
      id: 'analyze',
      label: 'Analyze',
      icon: Sparkles,
      description: 'AI is analyzing your document...',
    },
    {
      id: 'extract',
      label: 'Extract',
      icon: FileCheck,
      description: 'Extracting information...',
    },
  ];

  const CurrentIcon = steps[currentStep]?.icon || FileSearch;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="processing-animation-wrapper d-flex align-items-center justify-content-center"
    >
      <div className="processing-card bg-white w-100">
        <div className="d-flex flex-column align-items-center justify-content-center gap-4">
          {/* Animated Icon */}
          <div className="processing-icon-container position-relative">
            {/* Outer rotating ring */}
            <motion.div
              className="processing-rotating-ring position-absolute"
              animate={{ rotate: 360 }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear',
              }}
            >
              <div className="processing-ring-border w-100 h-100 rounded-circle" />
            </motion.div>

            {/* Inner pulsing circle */}
            <motion.div
              className="processing-pulsing-circle position-relative bg_master rounded-circle d-flex align-items-center justify-content-center"
              animate={{
                scale: [1, 1.1, 1],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              <CurrentIcon
                className="processing-main-icon text-white"
                strokeWidth={1.5}
              />
            </motion.div>

            {/* Floating particles */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="processing-particle position-absolute rounded-circle"
                animate={{
                  x: [0, Math.cos((i * 60 * Math.PI) / 180) * 60],
                  y: [0, Math.sin((i * 60 * Math.PI) / 180) * 60],
                  opacity: [1, 0],
                  scale: [1, 0],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  delay: i * 0.2,
                  ease: 'easeOut',
                }}
              />
            ))}
          </div>

          {/* Status Text */}
          <div className="text-center d-flex flex-column gap-2">
            <motion.h2
              key={currentStep}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="font_20 fw_400 text-dark mb-0"
            >
              {currentStep === 3
                ? 'Processing Complete!'
                : steps[currentStep]?.description}
            </motion.h2>
            <motion.p
              className="font_15 text-secondary mb-0"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            >
              {currentStep === 0
                ? 'Securing your document'
                : currentStep === 1
                ? 'Processing with AI'
                : currentStep === 2
                ? 'Finalizing extraction'
                : 'Preparing results...'}
            </motion.p>
          </div>

          {/* Progress Steps */}
          <div className="processing-steps-container w-100 d-flex flex-column gap-3 mt-2">
            {steps.map((step, index) => {
              const isDone = index < currentStep || currentStep === 3;
              const isActive = index === currentStep && currentStep < 3;

              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="d-flex align-items-center gap-3"
                >
                  <motion.div
                    animate={isDone ? { scale: [1, 1.2, 1] } : {}}
                    transition={{ duration: 0.3 }}
                    className={`processing-step-indicator rounded-circle d-flex align-items-center justify-content-center ${
                      isDone
                        ? 'bg-success'
                        : isActive
                        ? 'bg_master'
                        : 'bg-light'
                    }`}
                    style={{
                      boxShadow:
                        isDone || isActive
                          ? '0 4px 12px rgba(102, 126, 234, 0.4)'
                          : 'none',
                    }}
                  >
                    {isDone && (
                      <CheckCircle2
                        className="processing-step-icon text-white"
                        strokeWidth={2.5}
                      />
                    )}
                  </motion.div>
                  <div className="flex-grow-1">
                    <div className="processing-step-bar bg-light rounded-pill overflow-hidden">
                      <motion.div
                        className="h-100 bg_master"
                        initial={{ width: 0 }}
                        animate={{ width: isDone ? '100%' : '0%' }}
                        transition={{ duration: 0.5, delay: index * 0.2 }}
                      />
                    </div>
                  </div>
                  <span
                    className={`font_14 fw_500 ${
                      isDone ? 'text-dark' : 'text-muted'
                    }`}
                  >
                    {step.label}
                  </span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ProcessingAnimation;
